import { Link } from "wouter";
import { GlassCard, PillTag, Btn } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";
import {
  queries,
  mutations,
  useStoreQuery,
  type WelcomeStep,
} from "@/lib/store";

/**
 * AutoflowCard
 *
 * A compact, single-step pulse on Today that holds this morning's gentle
 * invitation from the seven-day welcome journey. Threshold language only:
 * "Plant the seed", "Bearing fruit". Never "Day 3 of 7" automation copy.
 *
 * Visible only when:
 *   - the journey is not yet complete, and
 *   - the sister has not dismissed the card.
 *
 * The full timeline lives at /welcome-journey.
 */
export default function AutoflowCard({
  userId,
}: {
  userId: string;
}) {
  const shouldShow = useStoreQuery(() =>
    queries.shouldShowAutoflowOnToday(userId),
  );
  const step = useStoreQuery(() => queries.welcomeCurrentStep(userId));
  const completedCount = useStoreQuery(() =>
    queries.welcomeCompletedCount(userId),
  ) ?? 0;
  const isPrimaryDone = useStoreQuery(() =>
    step ? queries.isWelcomeActionDone(userId, step.primaryAction.key) : false,
  ) ?? false;
  const isSecondaryDone = useStoreQuery(() =>
    step?.secondaryAction
      ? queries.isWelcomeActionDone(userId, step.secondaryAction.key)
      : false,
  ) ?? false;
  const isDayDone = useStoreQuery(() =>
    step ? queries.isWelcomeDayDone(userId, step.day) : false,
  ) ?? false;

  if (!shouldShow || !step) return null;

  const palette = palettes[step.tone];

  const onPrimary = () => {
    mutations.completeWelcomeAction({
      userId,
      actionKey: step.primaryAction.key,
    });
  };
  const onSecondary = () => {
    if (!step.secondaryAction) return;
    mutations.completeWelcomeAction({
      userId,
      actionKey: step.secondaryAction.key,
    });
  };
  const onDismiss = () => {
    mutations.dismissWelcomeAutoflow({ userId });
  };

  const primaryButton = (
    <Btn
      variant="primary"
      size="sm"
      block
      onClick={onPrimary}
      disabled={isPrimaryDone}
      data-testid={`button-autoflow-primary-${step.id}`}
    >
      {isPrimaryDone ? (
        <>
          <Icon name="check_circle" size={14} />
          {step.primaryAction.completedLabel}
        </>
      ) : (
        <>
          <Icon name="eco" size={14} />
          {step.primaryAction.label}
        </>
      )}
    </Btn>
  );

  return (
    <GlassCard
      organic
      padding={0}
      className="overflow-hidden"
      data-testid={`card-autoflow-${step.id}`}
    >
      {/* Hero band */}
      <div
        className="relative"
        style={{
          background: palette.heroBg,
          padding: "16px 18px 14px",
        }}
      >
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Hide welcome card"
          data-testid="button-autoflow-dismiss"
          className="absolute top-3 right-3 inline-flex items-center justify-center hover-elevate"
          style={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.6)",
            color: "var(--mnf-tg)",
            border: "none",
            cursor: "pointer",
          }}
        >
          <Icon name="close" size={14} />
        </button>

        <div className="flex items-start gap-3 pr-9">
          <div
            className="shrink-0 inline-flex items-center justify-center relative"
            style={{
              width: 48,
              height: 48,
              borderRadius: 16,
              background: "rgba(255,255,255,0.7)",
              fontSize: 24,
            }}
            aria-hidden
          >
            <span>{step.hero.primary}</span>
            {step.hero.secondary && (
              <span
                style={{
                  position: "absolute",
                  fontSize: 14,
                  transform: "translate(16px, -12px)",
                  opacity: 0.85,
                }}
              >
                {step.hero.secondary}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p
                className="mnf-eyebrow"
                style={{ color: palette.fg }}
                data-testid={`text-autoflow-eyebrow-${step.id}`}
              >
                {step.eyebrow}
              </p>
              <PillTag tone="neutral">{completedCount} of 7</PillTag>
            </div>
            <h3
              className="mnf-serif mt-0.5"
              style={{
                fontSize: 18,
                fontWeight: 500,
                color: "var(--mnf-fd)",
                lineHeight: 1.2,
              }}
              data-testid={`text-autoflow-title-${step.id}`}
            >
              {step.title}
            </h3>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-5 py-4">
        <p
          className="mnf-serif italic"
          style={{
            fontSize: 14,
            lineHeight: 1.5,
            color: "var(--mnf-fd)",
          }}
        >
          {`"${step.scripture}"`}
        </p>
        <p
          className="mnf-body mt-1.5"
          style={{
            fontSize: 11.5,
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: palette.fg,
          }}
        >
          {step.scriptureRef}
        </p>

        <p
          className="mnf-body mt-3"
          style={{
            fontSize: 13.5,
            lineHeight: 1.6,
            color: "var(--mnf-tx)",
            whiteSpace: "pre-line",
          }}
        >
          {step.body}
        </p>

        {/* Actions */}
        <div className="mt-4 flex flex-col gap-2">
          {step.primaryAction.kind === "navigate" && step.primaryAction.href ? (
            <Link
              href={step.primaryAction.href}
              onClick={onPrimary}
              className="no-underline block"
            >
              {primaryButton}
            </Link>
          ) : (
            primaryButton
          )}

          {step.primaryAction.helper && !isPrimaryDone && (
            <p
              className="mnf-body text-center"
              style={{
                fontSize: 11.5,
                color: "var(--mnf-tg)",
                fontStyle: "italic",
              }}
            >
              {step.primaryAction.helper}
            </p>
          )}

          {step.secondaryAction && (
            <Btn
              variant="ghost"
              size="sm"
              block
              onClick={onSecondary}
              disabled={isSecondaryDone}
              data-testid={`button-autoflow-secondary-${step.id}`}
            >
              {isSecondaryDone ? (
                <>
                  <Icon name="check" size={14} />
                  {step.secondaryAction.completedLabel}
                </>
              ) : (
                step.secondaryAction.label
              )}
            </Btn>
          )}
        </div>

        {isDayDone && (
          <div
            className="mt-4 text-center"
            style={{
              padding: "10px 14px",
              borderRadius: 14,
              background: palette.softBg,
            }}
          >
            <p
              className="mnf-serif italic"
              style={{
                fontSize: 13.5,
                color: "var(--mnf-fd)",
                lineHeight: 1.4,
              }}
              data-testid={`text-autoflow-reflection-${step.id}`}
            >
              {step.completedReflection}
            </p>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between">
          <Link
            href="/welcome-journey"
            className="mnf-body inline-flex items-center gap-1 no-underline"
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: palette.fg,
            }}
            data-testid="link-autoflow-journey"
          >
            See the full journey
            <Icon name="chevron_right" size={14} />
          </Link>
          <span
            className="mnf-body"
            style={{
              fontSize: 11,
              color: "var(--mnf-tg)",
              fontStyle: "italic",
            }}
          >
            Grace, not pressure
          </span>
        </div>
      </div>
    </GlassCard>
  );
}

/* ----------------------------- Tone palettes ------------------------------ */
type Tone = WelcomeStep["tone"];
const palettes: Record<
  Tone,
  { fg: string; heroBg: string; softBg: string }
> = {
  forest: {
    fg: "var(--mnf-f)",
    heroBg:
      "linear-gradient(135deg, rgba(30,74,53,0.10) 0%, rgba(245,240,232,0.72) 100%)",
    softBg: "rgba(30,74,53,0.06)",
  },
  coral: {
    fg: "var(--mnf-t)",
    heroBg:
      "linear-gradient(135deg, rgba(192,120,74,0.14) 0%, rgba(245,240,232,0.72) 100%)",
    softBg: "rgba(192,120,74,0.10)",
  },
  gold: {
    fg: "var(--mnf-gold)",
    heroBg:
      "linear-gradient(135deg, rgba(160,120,32,0.16) 0%, rgba(245,240,232,0.72) 100%)",
    softBg: "rgba(160,120,32,0.10)",
  },
  purple: {
    fg: "var(--mnf-purple)",
    heroBg:
      "linear-gradient(135deg, rgba(127,119,221,0.12) 0%, rgba(245,240,232,0.72) 100%)",
    softBg: "rgba(127,119,221,0.10)",
  },
  fiber: {
    fg: "#2C6248",
    heroBg:
      "linear-gradient(135deg, rgba(168,212,184,0.30) 0%, rgba(245,240,232,0.72) 100%)",
    softBg: "rgba(168,212,184,0.20)",
  },
};
